import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { useSiteConfig } from './SiteBanner';
import { CONSENT_OPEN_EVENT, hasGpc, isPrivatePath, loadGtm, normalizeGtmId, readConsent, saveConsent, setConsentDefaults } from '../lib/consent';

/** The admin-configured GTM container id from /api/site-config, or '' (no GTM → no banner, no cookie settings). */
export function useGtmId() {
  const config = useSiteConfig();
  return normalizeGtmId(config && config.analytics && config.analytics.gtmId);
}

/**
 * Small analytics-consent bar. Rendered only when a GTM container is configured: the site's own analytics are
 * cookieless and need no banner. GPC browsers are treated as "declined" and never see it.
 */
export default function ConsentBanner() {
  const gtmId = useGtmId();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const acceptRef = useRef(null);
  const privatePath = isPrivatePath(pathname);

  // Decide whether to ask, and load GTM when consent already exists.
  useEffect(() => {
    if (!gtmId) return;
    setConsentDefaults();
    if (hasGpc()) return;
    if (readConsent()) loadGtm(gtmId, pathname);
    else if (!privatePath) setOpen(true);
  }, [gtmId, pathname, privatePath]);

  // Footer "Cookie settings" reopens the banner.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (open && acceptRef.current && readConsent()) acceptRef.current.focus();
  }, [open]);

  if (!gtmId || !open) return null;

  const gpc = hasGpc();
  const choose = (analytics) => {
    saveConsent(analytics);
    setOpen(false);
    if (analytics) loadGtm(gtmId, pathname);
  };

  return (
    <section
      aria-label="Cookie consent"
      aria-describedby="consent-text"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-xl rounded-2xl border border-line/30 bg-surface p-4 text-ink shadow-lift sm:inset-x-auto sm:left-4 sm:right-auto"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-sky-soft text-primary" aria-hidden="true">
          <Cookie className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold">Analytics cookies</p>
          <p id="consent-text" className="mt-1 text-[13px] leading-relaxed text-muted">
            {gpc
              ? 'Your browser sends Global Privacy Control, so we treat it as “no” and never load optional analytics cookies.'
              : 'We’d like to use Google Tag Manager to measure how the site is used. It sets cookies only if you accept. Our own analytics are cookieless either way.'}{' '}
            <Link to="/privacy" className="font-semibold text-primary underline">
              Privacy
            </Link>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {gpc ? (
              <button type="button" className="btn-primary btn-sm" onClick={() => setOpen(false)}>
                OK
              </button>
            ) : (
              <>
                <button ref={acceptRef} type="button" className="btn-primary btn-sm" onClick={() => choose(true)}>
                  Accept
                </button>
                <button type="button" className="btn-outline btn-sm" onClick={() => choose(false)}>
                  Decline
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
